import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { collection, orderBy, QueryConstraint, where } from "firebase/firestore";
import { Anchor, Badge, Button, Group, Modal, Select, Stack, Table, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { IconCash, IconFileSpreadsheet, IconSearch } from "@tabler/icons-react";
import { useFirestore } from "../../helpers/firebaseContext";
import { CtcSettlement, CtcSettlementSide } from "../../types";
import { formatMoney, useCtcCallables, useCtcUser } from "./ctc_helpers";
import { useLivePagedQuery } from "./useLivePagedQuery";
import { CtcPager, CtcQueryError } from "./ctc_pager";
import { CtcViewOrderLink } from "./ctc_view_order_link";
import { CtcBulkSettleModal } from "./ctc_bulk_settle";
import { CtcPanel, ctcClearable } from "./ctc_panel";
import { Configure, InstantSearch, useHits, useInstantSearch } from "react-instantsearch";
import { CtcSettlementView, ctcSettlementSearchFilter, ctcSortedIndex } from "../../../../shared/ctcSearch";
import { useCtcListBackend, useDebounced, useMemoryState } from "./ctc_orders_table";
import {
    CTC_SETTLEMENTS_INDEX, CtcCreatedRange, CtcDayRange, CtcSearchError, CtcSearchInput, CtcSearchPager, CtcSearchRefresh,
    CtcSearchLiveRefresh, CtcSearchRefreshHandle, ctcSettlementFromHit, useCtcSearchClient, useCtcSearchMemory,
} from "./ctc_search";

/*
 * Transactions (phase 4, reduced): one row per confirmed purchase with the customer and broker
 * commission. Money moves outside the portal; back office records it with "Mark settled".
 * Live + cursor-paginated; every view is an indexed server-side query (ctc_settlements).
 */

type View = CtcSettlementView;
const PAGE_SIZE = 25;

const STATUS_COLOR: Record<string, string> = { settled: "teal", unsettled: "orange", waived: "gray", pending_waive_approval: "yellow" };
const STATUS_LABEL: Record<CtcSettlementSide["status"], string> = { settled: "Settled", unsettled: "Unsettled", waived: "Waived", pending_waive_approval: "Awaiting waiver approval" };

const when = (ts?: { toDate?: () => Date }) => ts?.toDate?.().toLocaleDateString("en-GB");

function SideCell({ side, canEdit, onSettle, onUndo }: { side?: CtcSettlementSide; canEdit: boolean; onSettle: () => void; onUndo: () => void }) {
    if (!side) return <Text size="sm" c="dimmed">—</Text>;
    return (
        <Stack gap={2}>
            <Group gap={6} wrap="nowrap">
                <Text size="sm" fw={600}>{side.amount !== undefined ? formatMoney(side.amount, side.currencyId ?? undefined) : "Not yet calculated"}</Text>
                <Badge size="xs" variant="light" color={STATUS_COLOR[side.status] ?? "gray"}>{STATUS_LABEL[side.status] ?? side.status}</Badge>
            </Group>
            {side.status === "settled" && (
                <Text size="xs" c="dimmed">
                    {when(side.settledAt)}{side.reference ? ` · ${side.reference}` : ""}{side.comment ? ` · ${side.comment}` : ""}{side.settledBy ? ` · ${side.settledBy.split("@")[0]}` : ""}
                    {canEdit && side.settledBy !== "system" && <Anchor size="xs" ml={6} onClick={onUndo}>undo</Anchor>}
                </Text>
            )}
            {side.status === "unsettled" && canEdit && side.amount !== undefined && (
                <Button size="compact-xs" variant="light" color="teal" onClick={onSettle} w="fit-content">Mark settled</Button>
            )}
        </Stack>
    );
}

const VIEWS: { value: View; label: string }[] = [
    { value: "open", label: "Unsettled commissions" },
    { value: "customer_unsettled", label: "Customer commission unsettled" },
    { value: "broker_unsettled", label: "Broker commission unsettled" },
    { value: "settled", label: "Fully settled" },
    { value: "all", label: "All" },
];

type OpenDialog = (d: { row: CtcSettlement; side: "customer" | "broker"; undo?: boolean }) => void;

function SettlementsTable({ rows, loading, canEdit, onDialog }: { rows: CtcSettlement[]; loading: boolean; canEdit: boolean; onDialog: OpenDialog }) {
    return (
        <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Order</Table.Th>
                    <Table.Th>Customer commission</Table.Th>
                    <Table.Th>Broker commission</Table.Th>
                    <Table.Th>Opened</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {/* A Firestore reference search isn't filtered by the query: hide soft-deleted settlements here */}
                {rows.filter((row) => !row.deleted).map((row) => (
                    <Table.Tr key={row.id}>
                        <Table.Td>
                            <Group gap={4} wrap="nowrap">
                                <CtcViewOrderLink poId={row.poId ?? row.id!} />
                                <Link to={`/ctc/orders/${row.poId ?? row.id}`}>{row.referenceNumber}</Link>
                                {row.orderState && <Badge size="xs" color="red" variant="light" ml={6}>Order cancelled</Badge>}
                            </Group>
                        </Table.Td>
                        <Table.Td><SideCell side={row.customer} canEdit={canEdit} onSettle={() => onDialog({ row, side: "customer" })} onUndo={() => onDialog({ row, side: "customer", undo: true })} /></Table.Td>
                        <Table.Td><SideCell side={row.broker} canEdit={canEdit} onSettle={() => onDialog({ row, side: "broker" })} onUndo={() => onDialog({ row, side: "broker", undo: true })} /></Table.Td>
                        <Table.Td>{when(row.created)}</Table.Td>
                    </Table.Tr>
                ))}
                {!rows.length && !loading && <Table.Tr><Table.Td colSpan={4}><Text size="sm" c="dimmed">No transactions match these filters.</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
        </Table>
    );
}

/** Firestore: live, cursor-paged; every view is an indexed server-side query. */
function FirestoreSettlements({ view, search, canEdit, onDialog }: { view: View; search: string; canEdit: boolean; onDialog: OpenDialog }) {
    const db = useFirestore();
    const user = useCtcUser();
    const ref = useMemo(() => collection(db, "ctc_settlements"), [db]);
    const { constraints, key } = useMemo(() => {
        const base: QueryConstraint[] = user.customerId ? [where("customerId", "==", user.customerId)] : [];
        if (search) {
            return { key: `ref:${search}:${user.customerId ?? ""}`, constraints: [...base, where("referenceNumber", ">=", search), where("referenceNumber", "<=", `${search}\uf8ff`), orderBy("referenceNumber")] };
        }
        const filter: QueryConstraint[] =
            view === "open" ? [where("settled", "==", false)] :
                view === "settled" ? [where("settled", "==", true)] :
                    view === "customer_unsettled" ? [where("customer.status", "==", "unsettled")] :
                        view === "broker_unsettled" ? [where("broker.status", "==", "unsettled")] : [];
        // Settlements of orders cancelled after completion are soft-deleted (deleted: true): left out by the query
        // itself, so they never fill a page. Uses index merging with the (deleted, created) index: no index per view.
        return { key: `${view}:${user.customerId ?? ""}`, constraints: [...base, where("deleted", "==", false), ...filter, orderBy("created", "desc")] };
    }, [view, search, user.customerId]);
    const page = useLivePagedQuery<CtcSettlement>(ref, constraints, key, PAGE_SIZE);
    return (
        <>
            <CtcQueryError error={page.error} />
            <SettlementsTable rows={page.rows} loading={page.loading} canEdit={canEdit} onDialog={onDialog} />
            <CtcPager page={page} />
        </>
    );
}

function TypesenseSettlements({ canEdit, onDialog }: { canEdit: boolean; onDialog: OpenDialog }) {
    const { items } = useHits();
    const { status } = useInstantSearch();
    const rows = useMemo(() => items.map((h) => ctcSettlementFromHit(h as unknown as Record<string, unknown>)), [items]);
    return <SettlementsTable rows={rows} loading={status !== "idle"} canEdit={canEdit} onDialog={onDialog} />;
}

const CtcTransactions = () => {
    const user = useCtcUser();
    const { markSettled } = useCtcCallables();
    const canEdit = user.has("backoffice") || user.isAdmin;
    const { backend, setBackend, control: backendSwitch } = useCtcListBackend();
    const [view, setView] = useMemoryState<View>("transactions:view", "open");
    const [searchInput, setSearchInput] = useState("");
    const search = useDebounced(searchInput.trim().toUpperCase());
    const [range, setRange] = useMemoryState<CtcDayRange>("ts:transactions:range", [null, null]);
    const searchClient = useCtcSearchClient("referenceNumber");
    const indexName = ctcSortedIndex(CTC_SETTLEMENTS_INDEX, "created", "desc");
    const memory = useCtcSearchMemory("transactions", indexName);
    // Typesense isn't live: search again shortly after a settlement changes (its copy updates within a second or two)
    const refreshSearch = useRef<(() => void) | undefined>(undefined);
    const onRefreshReady = useCallback((refresh: () => void) => { refreshSearch.current = refresh; }, []);

    const [dialog, setDialog] = useState<{ row: CtcSettlement; side: "customer" | "broker"; undo?: boolean } | null>(null);
    const [paymentRef, setPaymentRef] = useState("");
    const [settledOn, setSettledOn] = useState<Date | null>(new Date());
    const [busy, setBusy] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    const submit = async () => {
        if (!dialog) return;
        setBusy(true);
        try {
            await markSettled({
                poId: dialog.row.poId ?? dialog.row.id!,
                side: dialog.side,
                settled: !dialog.undo,
                reference: dialog.undo ? undefined : paymentRef,
                settledAt: dialog.undo || !settledOn ? undefined : settledOn.toISOString(),
            });
            notifications.show({ color: "green", message: dialog.undo ? "Settlement undone" : "Marked settled" });
            setDialog(null);
            setPaymentRef("");
            setTimeout(() => refreshSearch.current?.(), 1500);
        } catch (e) {
            notifications.show({ color: "red", message: (e as Error).message });
        } finally {
            setBusy(false);
        }
    };

    const viewSelect = (disabled = false) => (
        <Select label="Show" value={view} onChange={(v) => setView((v as View) ?? "open")} disabled={disabled} data={VIEWS} w={280} />
    );
    const toolbarRight = (
        <Group ml="auto" align="flex-end">
            {backendSwitch}
            {backend === "typesense" && <CtcSearchRefresh />}
            {canEdit && (
                <Button variant="light" leftSection={<IconFileSpreadsheet size={16} />} onClick={() => setBulkOpen(true)}>
                    Bulk settle (Excel)
                </Button>
            )}
        </Group>
    );

    return (
        <CtcPanel>
            {backend === "typesense" ? (
                <InstantSearch key={indexName} searchClient={searchClient} indexName={indexName} initialUiState={memory.initialUiState} onStateChange={memory.onStateChange}>
                    <Configure filters={ctcSettlementSearchFilter(view, { from: range[0], to: range[1] })} />
                    <CtcSearchRefreshHandle onReady={onRefreshReady} />
                    <CtcSearchLiveRefresh kind="settlements" />
                    <Group mb="md" align="flex-end">
                        {viewSelect()}
                        <CtcSearchInput placeholder="Reference" w={220} />
                        <CtcCreatedRange value={range} onChange={setRange} />
                        {toolbarRight}
                    </Group>
                    <CtcSearchError onUseFirestore={() => setBackend("firestore")} />
                    <TypesenseSettlements canEdit={canEdit} onDialog={setDialog} />
                    <CtcSearchPager />
                </InstantSearch>
            ) : (
                <>
                    <Group mb="md" align="flex-end">
                        {viewSelect(!!search)}
                        <TextInput label="Reference" leftSection={<IconSearch size={14} />} placeholder="KFH-26-00…" value={searchInput} onChange={(e) => setSearchInput(e.currentTarget.value)} w={220} {...ctcClearable(searchInput, () => setSearchInput(""))} />
                        {toolbarRight}
                    </Group>
                    <FirestoreSettlements view={view} search={search} canEdit={canEdit} onDialog={setDialog} />
                </>
            )}

            <Modal opened={!!dialog} onClose={() => !busy && setDialog(null)} title={dialog?.undo ? "Undo settlement" : `Mark ${dialog?.side} commission settled`}>
                {dialog && (
                    <Stack>
                        <Text size="sm">
                            {dialog.row.referenceNumber} · {dialog.side} commission {formatMoney(dialog.row[dialog.side]?.amount, dialog.row[dialog.side]?.currencyId ?? undefined)}
                        </Text>
                        {!dialog.undo && (
                            <>
                                <TextInput label="Payment reference (optional)" placeholder="core banking / transfer reference" value={paymentRef} onChange={(e) => setPaymentRef(e.currentTarget.value)} />
                                <DateInput label="Settled on" value={settledOn} onChange={(v) => setSettledOn(v ? new Date(v) : null)} maxDate={new Date()} />
                            </>
                        )}
                        <Group justify="flex-end">
                            <Button variant="default" onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
                            <Button color={dialog.undo ? "red" : "teal"} loading={busy} onClick={submit}>{dialog.undo ? "Undo" : "Mark settled"}</Button>
                        </Group>
                    </Stack>
                )}
            </Modal>
            {canEdit && <CtcBulkSettleModal opened={bulkOpen} onClose={() => { setBulkOpen(false); refreshSearch.current?.(); }} />}
        </CtcPanel>
    );
};

export default CtcTransactions;
